<template>
  <Modal
    :title="modalTitle"
    :subtitle="modalSubtitle"
    :is-cancellable="step !== 'progress'"
    :show-cancel-button="step !== 'progress'"
    :modal-buttons="modalButtons"
    :after-closed="afterClosed"
    :after-cancelled="afterCancelled"
  >
    <ImportOverview
      v-if="step === 'overview'"
      :status="status"
      :error-message="errorMessage"
      :subtitle="subtitle"
      :entry-title="entryTitle"
      :primary-screenshot-url="primaryScreenshotUrl"
      :screenshot-urls="screenshotUrls"
      :category-labels="categoryLabels"
      :creator-label="creatorLabel"
      :available-label="availableLabel"
      :certified-label="certifiedLabel"
      :description-text="descriptionText"
      :content-summary="contentSummary"
    />
    <ImportConfigure
      v-else-if="step === 'configure'"
      :pkg="contentJson"
      :org-id="orgId"
      :workspace-id="workspaceId"
      :automation-agent-id="rootAutomationAgentId"
      :destination-locked="personalOnly"
      :db-items="dbSummaryItems"
      :workspace-db-dict="effectiveWorkspaceDbDict"
      :mode-by-db-id="modeByDbId"
      :target-by-db-id="targetByDbId"
      :import-objects-enabled-by-db-id="importObjectsEnabledByDbId"
      :set-import-objects-enabled="setImportObjectsEnabled"
      :set-mode="setMode"
      :set-target="setTarget"
      @update:orgId="orgId = $event"
      @update:workspaceId="workspaceId = $event"
      @update:automationAgentId="rootAutomationAgentId = $event"
    />
    <ImportProgress
      v-else
      :message="progressMessage"
      :done="progressDone"
      :total="progressTotal"
      :error="installError ?? undefined"
    />
  </Modal>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
  watch,
} from "vue";

import type {
  AnyAgentId,
  GraphDatabase,
  GraphDatabaseDictionary,
  OrgId,
  TableId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import type { ButtonState } from "@/components/base/BaseButton.vue";
import { isDefaultExtensionTableId } from "@/components/databases";
import * as inject from "@/components/inject";
import {
  parseMarketplaceCategoryFields,
  parseMarketplaceCreatorFields,
  parseMarketplaceExtensionFields,
  type MarketplaceCategoryFields,
  type MarketplaceCreatorFields,
  type MarketplaceExtensionFields,
} from "@/components/marketplace/contentful-fields";
import {
  importPackage,
  prepareInstall,
  type ImportProgressCallback,
  type InstallPlan,
} from "@/components/marketplace/import";
import ImportConfigure from "@/components/marketplace/ImportConfigure.vue";
import ImportOverview from "@/components/marketplace/ImportOverview.vue";
import ImportProgress from "@/components/marketplace/ImportProgress.vue";
import { loadPackage } from "@/components/marketplace/package";
import {
  collectAgents,
  collectAutomations,
  collectPackageDefaultEntities,
  eachPackagedObject,
  packagedTreesOf,
} from "@/components/marketplace/traverse";
import type { Package } from "@/components/marketplace/types";
import { typeDiff } from "@/components/marketplace/utils";
import { applyViewConfigurations } from "@/components/marketplace/view-configurations";
import Modal, { type ModalButtons } from "@/components/modals/Modal.vue";
import { useBrowserStore } from "@/composables/useBrowserStore";
import {
  ContentfulAsset,
  getAsset,
  getAssetUrl,
  getEntry,
} from "@/utils/contentful";
import { isJsonRecord } from "@/utils/json";
import {
  navigateToAgent,
  navigateToAutomation,
  navigateToId,
  navigateToObject,
} from "@/utils/navigate";

export default defineComponent({
  name: "ImportModal",
  components: { ImportConfigure, ImportOverview, ImportProgress, Modal },
  props: {
    extensionId: { type: String, required: false },
    afterClosed: { type: Function as PropType<() => void>, required: false },
    afterCancelled: {
      type: Function as PropType<() => void>,
      required: false,
    },
  },
  setup(props) {
    const logger = inject.logger();
    const controller = inject.controller();
    const router = inject.router();
    const profile = inject.profile();
    const browserStore = useBrowserStore(profile.value.user.accountId);
    const defaultTypes = inject.personalDefaultTypes();
    const status = ref<"loading" | "ready" | "error">("loading");
    const errorMessage = ref<string | null>(null);
    const step = ref<"overview" | "configure" | "progress">("overview");
    const orgId = ref<OrgId | undefined>(undefined);
    const workspaceId = ref<WorkspaceId | undefined>(undefined);
    // Agent to own the imported root automation (root-automation packages
    // only) — the exported package never carries the source account's agent.
    const rootAutomationAgentId = ref<AnyAgentId | null>(null);

    const entry = ref<MarketplaceExtensionFields | null>(null);
    const screenshots = ref<ContentfulAsset[]>([]);
    const creatorEntry = ref<MarketplaceCreatorFields | null>(null);
    const categoryEntries = ref<MarketplaceCategoryFields[]>([]);
    const contentJson = ref<Package | null>(null);

    const subtitle = computed(() => {
      if (props.extensionId) return `Extension ${props.extensionId}`;
      return null;
    });

    const modalTitle = computed(() => {
      switch (step.value) {
        case "configure":
          return "Configure import";
        case "progress":
          return "Installing…";
        default:
          return "Import";
      }
    });

    const modalSubtitle = computed(() => {
      if (step.value !== "overview") return undefined;
      return entryTitle.value ?? subtitle.value ?? undefined;
    });

    const entryTitle = computed(() => entry.value?.name || null);

    const creatorLabel = computed(() => {
      const fields = creatorEntry.value;
      return fields?.fullname || fields?.username || null;
    });

    const categoryLabels = computed(() =>
      categoryEntries.value
        .map((fields) => fields.name)
        .filter((v): v is string => typeof v === "string" && v.length > 0)
    );

    const availableLabel = computed(() => {
      const v = entry.value?.available;
      if (v === true) return "Yes";
      if (v === false) return "No";
      return "—";
    });

    const certifiedLabel = computed(() => {
      const v = entry.value?.certified;
      if (v === true) return "Yes";
      if (v === false) return "No";
      return "—";
    });

    const descriptionText = computed(() => null);

    const screenshotUrls = computed(() =>
      screenshots.value.map((a) => getAssetUrl(a))
    );
    const primaryScreenshotUrl = computed(
      () => screenshotUrls.value[0] || null
    );

    const contentSummary = computed(() => {
      const pkg = contentJson.value;
      if (!pkg) return null;
      const dbs = Object.keys(pkg.databases || {}).length;
      const objects = Object.values(pkg.databases || {}).reduce(
        (sum, db) => sum + (Array.isArray(db.rows) ? db.rows.length : 0),
        0
      );
      const agents = collectAgents(pkg.root).length;
      const automations = collectAutomations(pkg.root).length;
      const entities = collectPackageDefaultEntities(pkg);
      const tasks = entities.filter((e) => e.kind === "task").length;
      const pages = entities.filter((e) => e.kind === "page").length;

      const pluralize = (n: number, singular: string) =>
        `${n} ${singular}${n === 1 ? "" : "s"}`;

      // Steps are implicitly part of automations, so they are not listed.
      // Zero-count parts are omitted.
      const parts: string[] = [];
      if (dbs > 0) parts.push(pluralize(dbs, "database"));
      if (objects > 0) parts.push(pluralize(objects, "object"));
      if (tasks > 0) parts.push(pluralize(tasks, "task"));
      if (pages > 0) parts.push(pluralize(pages, "page"));
      if (agents > 0) parts.push(pluralize(agents, "agent"));
      if (automations > 0) parts.push(pluralize(automations, "automation"));
      return parts.join(" · ");
    });

    // Built-in tasks/pages are personal-organization objects, so a package
    // containing any installs into the personal org/workspace only: the
    // destination is pinned (the pickers are hidden) and `importPackage`
    // enforces the invariant itself.
    const personalOnly = ref(false);

    watch(orgId, () => {
      workspaceId.value = personalOnly.value
        ? profile.value.user.personal.workspaceId
        : undefined;
    });

    const workspaceDatabases = inject.workspaceDatabases();
    const workspaceDbDict = computed<GraphDatabaseDictionary | undefined>(
      () => {
        const wsId = workspaceId.value;
        if (!wsId) return undefined;
        return workspaceDatabases.value?.[wsId];
      }
    );

    // Fetch workspace database schemas from the controller (more complete/consistent than the cached dict).
    const fetchedWorkspaceDbById = ref<GraphDatabaseDictionary>({});
    const fetchWorkspaceDb = async (
      wsId: WorkspaceId,
      dbId: string
    ): Promise<GraphDatabase | null> => {
      try {
        return (await controller.graph.database.get(wsId, dbId)).release();
      } catch {
        return null;
      }
    };

    const effectiveWorkspaceDbDict = computed<GraphDatabaseDictionary>(() => ({
      ...(workspaceDbDict.value ?? {}),
      ...fetchedWorkspaceDbById.value,
    }));

    // Resolve the effective use/extend destination for a packaged database.
    // Default-type extension tables (Task/Event/Page/Person…) are never valid
    // destinations: their rows are managed by dedicated RPCs + extension rows,
    // which the generic row import cannot target.
    const resolveTargetDb = (
      dict: GraphDatabaseDictionary,
      packagedDbId: string,
      target: string | undefined
    ): GraphDatabase | undefined => {
      const db =
        dict[packagedDbId] ?? (target ? dict[String(target)] : undefined);
      if (!db) return undefined;
      return isDefaultExtensionTableId(String(db.id)) ? undefined : db;
    };

    type Mode = "create" | "use" | "extend";
    const modeByDbId = ref<Record<string, Mode>>({});
    const targetByDbId = ref<Record<string, TableId | undefined>>({});
    const touchedByDbId = ref<Record<string, boolean>>({});
    const importObjectsEnabledByDbId = ref<Record<string, boolean>>({});

    const normalize = (s: string): string => s.trim().toLowerCase();
    const packagedDbName = (pkg: Package, id: string): string => {
      const db = pkg.databases?.[id];
      const schemaAny = db?.schema ?? null;
      const schemaRec = schemaAny && isJsonRecord(schemaAny) ? schemaAny : null;
      return (
        (schemaRec && typeof schemaRec["name"] === "string"
          ? schemaRec["name"]
          : null) ??
        (id || "Database")
      );
    };

    const packagedDatabaseIds = computed(() =>
      Object.keys(contentJson.value?.databases ?? {})
    );

    const requiredPackagedDbIds = computed(() => {
      const pkg = contentJson.value;
      const out = new Set<string>();
      if (!pkg) return out;

      for (const tree of packagedTreesOf(pkg)) {
        eachPackagedObject(tree, (obj) => {
          out.add(obj.meta.tableId);
        });
      }

      return out;
    });

    watch(
      contentJson,
      () => {
        const pkg = contentJson.value;
        if (!pkg) {
          importObjectsEnabledByDbId.value = {};
          return;
        }
        const required = requiredPackagedDbIds.value;
        const next = { ...importObjectsEnabledByDbId.value };
        for (const id of Object.keys(pkg.databases ?? {})) {
          if (next[id] === undefined) next[id] = true;
          if (required.has(id)) next[id] = true;
        }
        for (const k of Object.keys(next)) {
          if (!(k in (pkg.databases ?? {}))) delete next[k];
        }
        importObjectsEnabledByDbId.value = next;
      },
      { immediate: true }
    );

    watch(
      [workspaceId, contentJson, workspaceDbDict],
      () => {
        const pkg = contentJson.value;
        const dict = workspaceDbDict.value ?? {};
        if (!pkg) return;

        // Build an index of workspace databases by internal name (`db.name`) for default mapping.
        // Only auto-map when the match is unique.
        // Default-type extension tables (Task/Event/Page/Person…) are excluded:
        // their data is managed by dedicated RPCs + extension rows, which the
        // generic row import cannot target.
        const byWorkspaceName = (() => {
          const m = new Map<string, TableId[]>();
          for (const db of Object.values(dict)) {
            if (isDefaultExtensionTableId(String(db.id))) continue;
            const n = typeof db.name === "string" ? normalize(db.name) : "";
            if (!n) continue;
            const arr = m.get(n) ?? [];
            arr.push(db.id);
            m.set(n, arr);
          }
          return m;
        })();

        const nextMode: Record<string, Mode> = { ...modeByDbId.value };
        const nextTarget: Record<string, TableId | undefined> = {
          ...targetByDbId.value,
        };
        const nextTouched: Record<string, boolean> = { ...touchedByDbId.value };

        for (const id of packagedDatabaseIds.value) {
          const idExists = Boolean(dict[id]) && !isDefaultExtensionTableId(id);
          const touched = Boolean(nextTouched[id]);
          if (idExists && !touched) {
            const workspaceDb = dict[id];
            const diff = typeDiff({
              packagedSchema: pkg.databases[id].schema,
              workspaceSchema: workspaceDb,
            });
            nextMode[id] = diff.some((p) => p.willAdd) ? "extend" : "use";
            nextTarget[id] = id;
            continue;
          }

          if (!touched) {
            const name = normalize(packagedDbName(pkg, id));
            const candidates = byWorkspaceName.get(name) ?? [];
            if (candidates.length === 1) {
              const targetId = candidates[0];
              const workspaceDb = dict[String(targetId)];
              if (workspaceDb) {
                const diff = typeDiff({
                  packagedSchema: pkg.databases[id].schema,
                  workspaceSchema: workspaceDb,
                });
                nextMode[id] = diff.some((p) => p.willAdd) ? "extend" : "use";
                nextTarget[id] = targetId;
                continue;
              }
            }
          }

          if (!nextMode[id]) nextMode[id] = "create";
          if (nextMode[id] === "create") nextTarget[id] = undefined;
        }

        const valid = new Set(packagedDatabaseIds.value);
        for (const k of Object.keys(nextMode))
          if (!valid.has(k)) delete nextMode[k];
        for (const k of Object.keys(nextTarget))
          if (!valid.has(k)) delete nextTarget[k];
        for (const k of Object.keys(nextTouched))
          if (!valid.has(k)) delete nextTouched[k];

        modeByDbId.value = nextMode;
        targetByDbId.value = nextTarget;
        touchedByDbId.value = nextTouched;
      },
      { immediate: true }
    );

    // Populate `fetchedWorkspaceDbById` for all selected use targets.
    let fetchSeq = 0;
    watch(
      [workspaceId, contentJson, modeByDbId, targetByDbId, workspaceDbDict],
      async () => {
        const wsId = workspaceId.value;
        const pkg = contentJson.value;
        if (!wsId || !pkg) {
          fetchedWorkspaceDbById.value = {};
          return;
        }
        const dict = workspaceDbDict.value ?? {};
        const wanted = new Set<string>();
        for (const id of Object.keys(pkg.databases ?? {})) {
          const mode = modeByDbId.value[id] ?? "create";
          if (mode === "create") continue;
          if (dict[id]) wanted.add(id);
          const target = targetByDbId.value[id];
          if (target) wanted.add(String(target));
        }

        const seq = ++fetchSeq;
        const entries = await Promise.all(
          Array.from(wanted.values()).map(
            async (dbId) => [dbId, await fetchWorkspaceDb(wsId, dbId)] as const
          )
        );
        if (seq !== fetchSeq) return;
        const next: GraphDatabaseDictionary = {};
        for (const [dbId, db] of entries) {
          if (db) next[dbId] = db;
        }
        fetchedWorkspaceDbById.value = next;
      },
      { immediate: true }
    );

    // If a default use target has schema conflicts, switch default to creating a new database
    // without marking the mapping as "touched" (user hasn't interacted).
    watch(
      [
        workspaceId,
        contentJson,
        effectiveWorkspaceDbDict,
        modeByDbId,
        targetByDbId,
        touchedByDbId,
      ],
      () => {
        const wsId = workspaceId.value;
        const pkg = contentJson.value;
        if (!wsId || !pkg) return;
        const dict = effectiveWorkspaceDbDict.value ?? {};

        const nextMode: Record<string, Mode> = { ...modeByDbId.value };
        const nextTarget: Record<string, TableId | undefined> = {
          ...targetByDbId.value,
        };
        let changed = false;

        for (const id of Object.keys(pkg.databases ?? {})) {
          if (touchedByDbId.value[id]) continue;
          const mode = nextMode[id] ?? "create";
          if (mode === "create") continue;

          const target = nextTarget[id];
          const workspaceDb = resolveTargetDb(dict, id, target);
          if (!workspaceDb) continue;

          const diff = typeDiff({
            packagedSchema: pkg.databases[id].schema,
            workspaceSchema: workspaceDb,
          });
          if (diff.some((p) => p.isConflict)) {
            nextMode[id] = "create";
            nextTarget[id] = undefined;
            changed = true;
          }
        }

        if (changed) {
          modeByDbId.value = nextMode;
          targetByDbId.value = nextTarget;
        }
      },
      { immediate: true }
    );

    const setMode = (id: string, mode: Mode) => {
      modeByDbId.value = { ...modeByDbId.value, [id]: mode };
      touchedByDbId.value = { ...touchedByDbId.value, [id]: true };
      if (mode === "create") {
        targetByDbId.value = { ...targetByDbId.value, [id]: undefined };
      }
    };

    const setTarget = (id: string, target: TableId | undefined) => {
      targetByDbId.value = { ...targetByDbId.value, [id]: target };
      touchedByDbId.value = { ...touchedByDbId.value, [id]: true };
    };

    const setImportObjectsEnabled = (id: string, enabled: boolean) => {
      if (!enabled && requiredPackagedDbIds.value.has(id)) return;
      importObjectsEnabledByDbId.value = {
        ...importObjectsEnabledByDbId.value,
        [id]: enabled,
      };
    };

    const emptyInstallPlan: InstallPlan = {
      databases: { create: [], extend: [], use: [] },
      objects: { total: 0, byType: [] },
    };

    const installPlan = computed((): InstallPlan => {
      const pkg = contentJson.value;
      if (!pkg || !workspaceId.value) return emptyInstallPlan;

      const targetByDbIdStrings: Record<string, string | undefined> = {};
      for (const [k, v] of Object.entries(targetByDbId.value)) {
        targetByDbIdStrings[k] = v ? String(v) : undefined;
      }

      return prepareInstall(
        pkg,
        effectiveWorkspaceDbDict.value,
        modeByDbId.value,
        targetByDbIdStrings,
        importObjectsEnabledByDbId.value
      );
    });

    type DbSummaryItem = {
      packagedDbId: string;
      packagedName: string;
      packagedIcon: string | null;
      workspaceName: string | null;
      workspaceIcon: string | null;
      mode: "create" | "extend" | "use";
      willAddCount: number;
      isMissingMapping: boolean;
      isRequired: boolean;
      objectsCount: number;
      sortKey: string;
    };

    const dbSummaryItems = computed<DbSummaryItem[]>(() => {
      const pkg = contentJson.value;
      const wsId = workspaceId.value;
      if (!pkg || !wsId) return [];
      const dict = effectiveWorkspaceDbDict.value ?? {};

      // Count objects that are encoded in the "main hierarchy" (`pkg.root`) per packaged DB id.
      // Those are not necessarily present in `pkg.databases[...].rows` (e.g. if export chose
      // to only include schemas/rows for view/relation databases).
      const hierarchyRowIdsByDbId = new Map<string, Set<string>>();
      eachPackagedObject(pkg.root, (obj) => {
        const { tableId, rowId } = obj.meta;
        const set = hierarchyRowIdsByDbId.get(tableId) ?? new Set<string>();
        set.add(rowId);
        hierarchyRowIdsByDbId.set(tableId, set);
      });

      const out: DbSummaryItem[] = [];
      for (const packagedDbId of Object.keys(pkg.databases ?? {})) {
        const requested = modeByDbId.value[packagedDbId] ?? "create";
        const packagedSchema = pkg.databases[packagedDbId]?.schema ?? null;
        const target = targetByDbId.value[packagedDbId];
        const workspaceDb =
          requested === "create"
            ? null
            : (resolveTargetDb(dict, packagedDbId, target) ?? null);
        const hierarchyRowIds =
          hierarchyRowIdsByDbId.get(packagedDbId) ?? new Set<string>();
        const hierarchyCount = hierarchyRowIds.size;
        const isRequired = hierarchyCount > 0;
        const includeRows =
          isRequired ||
          importObjectsEnabledByDbId.value[packagedDbId] !== false;
        const extraRowCount = includeRows
          ? (pkg.databases?.[packagedDbId]?.rows ?? []).reduce((sum, r) => {
              if (hierarchyRowIds.has(r.id)) return sum;
              return sum + 1;
            }, 0)
          : 0;
        const objectsCount = includeRows ? hierarchyCount + extraRowCount : 0;

        const packagedName = (() => {
          const schemaRec =
            packagedSchema && isJsonRecord(packagedSchema)
              ? packagedSchema
              : null;
          const dn = schemaRec ? schemaRec["data_name"] : null;
          const n = schemaRec ? schemaRec["name"] : null;
          return (
            (typeof dn === "string" && dn) ||
            (typeof n === "string" && n) ||
            packagedDbId
          );
        })();

        const packagedIcon = (() => {
          const schemaRec =
            packagedSchema && isJsonRecord(packagedSchema)
              ? packagedSchema
              : null;
          const meta =
            schemaRec && isJsonRecord(schemaRec["meta"])
              ? schemaRec["meta"]
              : null;
          const emoji = meta ? meta["emoji"] : null;
          return typeof emoji === "string" ? emoji : null;
        })();

        const isMissingMapping = requested !== "create" && !workspaceDb;
        if (requested === "create" || !workspaceDb) {
          out.push({
            packagedDbId,
            packagedName,
            packagedIcon,
            workspaceName: null,
            workspaceIcon: null,
            mode: "create",
            willAddCount: 0,
            isMissingMapping,
            isRequired,
            objectsCount,
            sortKey: packagedName,
          });
          continue;
        }

        const workspaceName =
          workspaceDb.data_name || workspaceDb.name || String(workspaceDb.id);
        const workspaceIcon = workspaceDb.meta?.emoji ?? null;
        const diff = typeDiff({ packagedSchema, workspaceSchema: workspaceDb });
        const willAddCount = diff.filter((p) => p.willAdd).length;
        out.push({
          packagedDbId,
          packagedName,
          packagedIcon,
          workspaceName,
          workspaceIcon,
          mode: willAddCount > 0 ? "extend" : "use",
          willAddCount,
          isMissingMapping: false,
          isRequired,
          objectsCount,
          sortKey: workspaceName,
        });
      }

      out.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
      return out;
    });

    const load = async () => {
      logger.debug("load()", props.extensionId);
      status.value = "loading";
      errorMessage.value = null;
      entry.value = null;
      screenshots.value = [];
      creatorEntry.value = null;
      categoryEntries.value = [];
      contentJson.value = null;
      step.value = "overview";
      personalOnly.value = false;
      orgId.value = undefined;
      workspaceId.value = undefined;
      rootAutomationAgentId.value = null;

      try {
        const id = props.extensionId;
        if (!id) throw new Error("Missing extensionId.");

        const extensionEntry = await getEntry(id);
        entry.value = parseMarketplaceExtensionFields(extensionEntry.fields);
        if (!entry.value) throw new Error("Invalid marketplace extension.");

        const pkgJson = await loadPackage(id);
        if (!pkgJson) throw new Error("Invalid package.json format.");
        contentJson.value = pkgJson;
        personalOnly.value = collectPackageDefaultEntities(pkgJson).length > 0;
        if (personalOnly.value) {
          orgId.value = profile.value.user.personal.organizationId;
          workspaceId.value = profile.value.user.personal.workspaceId;
        }

        if (entry.value.screenshots.length > 0) {
          const results = await Promise.allSettled(
            entry.value.screenshots.map((link) => getAsset(link.sys.id))
          );
          screenshots.value = results
            .filter(
              (r): r is PromiseFulfilledResult<ContentfulAsset> =>
                r.status === "fulfilled"
            )
            .map((r) => r.value);
        }

        const creatorId = entry.value.creator?.sys.id;
        const categoryIds = entry.value.categories.map((link) => link.sys.id);
        const [creatorRes, categoriesRes] = await Promise.allSettled([
          creatorId
            ? getEntry(creatorId).then((creator) =>
                parseMarketplaceCreatorFields(creator.fields)
              )
            : Promise.resolve(null),
          Promise.allSettled(
            categoryIds.map((categoryId) =>
              getEntry(categoryId).then((category) =>
                parseMarketplaceCategoryFields(category.fields)
              )
            )
          ),
        ]);

        if (creatorRes.status === "fulfilled")
          creatorEntry.value = creatorRes.value;
        if (categoriesRes.status === "fulfilled") {
          categoryEntries.value = categoriesRes.value.flatMap((result) =>
            result.status === "fulfilled" && result.value ? [result.value] : []
          );
        }

        status.value = "ready";
      } catch (e) {
        status.value = "error";
        errorMessage.value = e instanceof Error ? e.message : String(e);
      }
    };

    onMounted(() => {
      void load();
    });

    const overviewContinueState = computed<ButtonState>(() => {
      if (status.value === "ready" && contentJson.value) return "default";
      if (status.value === "error") return "disabled";
      return "loading";
    });

    const installing = ref(false);
    const installError = ref<string | null>(null);
    const progressDone = ref(0);
    const progressTotal = ref(0);
    const progressMessage = ref("Waiting…");
    const progressCloseState = computed<ButtonState>(() =>
      installing.value ? "hidden" : "default"
    );

    const onProgress: ImportProgressCallback = (p) => {
      progressDone.value = p.done;
      progressTotal.value = p.total;
      progressMessage.value = p.message;
    };

    const configureInstallState = computed<ButtonState>(() => {
      if (!orgId.value || !workspaceId.value) return "disabled";
      if (installing.value) return "loading";
      return installEnabledState.value;
    });

    const hasSchemaConflicts = computed(() => {
      const pkg = contentJson.value;
      const dict = effectiveWorkspaceDbDict.value ?? {};
      if (!pkg || !workspaceId.value) return false;
      for (const id of Object.keys(pkg.databases ?? {})) {
        const mode = modeByDbId.value[id] ?? "create";
        if (mode === "create") continue;
        const target = targetByDbId.value[id];
        const workspaceDb = resolveTargetDb(dict, id, target);
        if (!workspaceDb) continue;
        const diff = typeDiff({
          packagedSchema: pkg.databases[id].schema,
          workspaceSchema: workspaceDb,
        });
        if (diff.some((p) => p.isConflict)) return true;
      }
      return false;
    });

    const installEnabledState = computed<ButtonState>(() => {
      if (!orgId.value || !workspaceId.value || !contentJson.value)
        return "disabled";
      if (hasSchemaConflicts.value) return "disabled";
      const pkg = contentJson.value;
      for (const id of Object.keys(pkg.databases ?? {})) {
        const mode = modeByDbId.value[id] ?? "create";
        if (mode !== "create") {
          const target = targetByDbId.value[id];
          const dict = effectiveWorkspaceDbDict.value ?? {};
          const hasMapping = Boolean(resolveTargetDb(dict, id, target));
          if (!hasMapping) return "disabled";
        }
      }
      return "default";
    });

    const runInstall = async (): Promise<boolean> => {
      const wsId = workspaceId.value;
      const pkg = contentJson.value;
      if (!wsId || !pkg) return false;
      if (configureInstallState.value !== "default") return false;

      step.value = "progress";
      installing.value = true;
      installError.value = null;
      progressDone.value = 0;
      progressTotal.value = 0;
      progressMessage.value = "Preparing install…";
      await nextTick();
      try {
        const res = await importPackage(
          controller,
          logger,
          wsId,
          pkg,
          effectiveWorkspaceDbDict.value,
          modeByDbId.value,
          targetByDbId.value,
          importObjectsEnabledByDbId.value,
          onProgress,
          orgId.value,
          profile.value.user.personal.workspaceId,
          rootAutomationAgentId.value
        );
        // Seed the packaged view display preferences (layout, group
        // display, …) before the imported blocks first render, keyed by
        // the fresh block ids the import generated — see
        // view-configurations.ts.
        applyViewConfigurations(browserStore, pkg, res.blockIdMap);
        const importedRoot = res.root;
        if (importedRoot?.kind === "agent") {
          await navigateToAgent(router, importedRoot.id);
        } else if (importedRoot?.kind === "automation") {
          await navigateToAutomation(router, importedRoot.id, orgId.value);
        } else if (
          importedRoot?.kind === "task" ||
          importedRoot?.kind === "page"
        ) {
          await navigateToObject(router, controller, {
            kind: importedRoot.kind,
            id: importedRoot.id,
          });
        } else if (res.rootObject) {
          await navigateToId(
            router,
            controller,
            defaultTypes.value ?? {},
            wsId,
            res.rootObject.databaseId,
            res.rootObject.id
          );
        }
        return true;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        logger.error("ImportModal", "importPackage failed", msg);
        errorMessage.value = msg;
        installError.value = msg;
        return false;
      } finally {
        installing.value = false;
      }
    };

    // With the destination pinned (personal-only package) and no databases to
    // map, the configure step would be empty — install straight from the
    // overview instead.
    const skipConfigure = computed<boolean>(() => {
      const pkg = contentJson.value;
      if (!pkg) return false;
      return (
        personalOnly.value && Object.keys(pkg.databases ?? {}).length === 0
      );
    });

    const modalButtons = computed<ModalButtons>(() => {
      switch (step.value) {
        case "overview":
          return [
            {
              title: skipConfigure.value ? "Install" : "Continue",
              kind: "main" as const,
              type: "primary" as const,
              state: overviewContinueState,
              func: async () => {
                if (skipConfigure.value) return runInstall();
                step.value = "configure";
                return false;
              },
            },
          ];
        case "configure":
          return [
            {
              title: "Back",
              kind: "main" as const,
              type: "tertiary" as const,
              align: "left" as const,
              func: async () => {
                step.value = "overview";
                return false;
              },
            },
            {
              title: "Install",
              kind: "main" as const,
              type: "primary" as const,
              state: configureInstallState,
              func: runInstall,
            },
          ];
        case "progress":
          return [
            {
              title: "Close",
              kind: "main" as const,
              type: "primary" as const,
              state: progressCloseState,
              func: async () => true,
            },
          ];
      }
      return [];
    });

    return {
      availableLabel,
      categoryLabels,
      certifiedLabel,
      contentJson,
      contentSummary,
      creatorLabel,
      descriptionText,
      entryTitle,
      errorMessage,
      installError,
      installPlan,
      dbSummaryItems,
      effectiveWorkspaceDbDict,
      modalButtons,
      modalSubtitle,
      modalTitle,
      orgId,
      personalOnly,
      primaryScreenshotUrl,
      progressDone,
      progressMessage,
      progressTotal,
      rootAutomationAgentId,
      screenshotUrls,
      status,
      step,
      subtitle,
      workspaceDbDict,
      workspaceId,
      modeByDbId,
      targetByDbId,
      importObjectsEnabledByDbId,
      setImportObjectsEnabled,
      setMode,
      setTarget,
    };
  },
});
</script>
